import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Grid,
  Header,
  Icon,
  Segment,
  Divider,
  Loader,
  Modal,
  Button,
} from "semantic-ui-react";
import { userApi } from "@api/users/user";
import { profileApi } from "@api/profile/profile";
import FacultyLibraryIdSection from "@authentication/pages/Profile/ProfileForm/FacultyLibraryIdSection";
import { getFullImageUrl } from "@utils/mobileUtils";

const AVATAR_STYLE = {
  width: "120px",
  height: "120px",
  borderRadius: "50%",
  backgroundColor: "#f4b952",
  margin: "0 auto 1rem auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #f4b952",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  overflow: "hidden",
  position: "relative",
};

const IMAGE_STYLE = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const LOADING_OVERLAY_STYLE = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(244, 185, 82, 0.8)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const USER_ICON_STYLE = {
  color: "#fff",
  marginTop: "67px",
  fontSize: "100px",
};

const getLibraryId = (profile) => {
  return (
    profile.library_id ||
    profile.library_no ||
    profile.lrc_no ||
    profile.lc_no ||
    profile.libraryId ||
    profile.library_number ||
    profile.libraryNumber
  );
};

// Helper function to get the specific user library type (Faculty, Visitor, or Researcher)
const getUserLibraryType = (userData) => {
  // Priority 1: Check user_type field
  if (userData?.user_type) {
    const userType = userData.user_type.trim().toLowerCase();
    return userType.charAt(0).toUpperCase() + userType.slice(1);
  }

  // Priority 2: Check role field (singular)
  if (userData?.role) {
    const role = userData.role.trim().toLowerCase();
    return role.charAt(0).toUpperCase() + role.slice(1);
  }

  // Priority 3: Check roles field (plural) - can be string or array
  if (userData?.roles) {
    let rolesStr = "";

    if (typeof userData.roles === "string") {
      rolesStr = userData.roles.trim().toLowerCase();
    } else if (Array.isArray(userData.roles) && userData.roles.length > 0) {
      const firstRole = userData.roles[0];
      rolesStr =
        typeof firstRole === "string"
          ? firstRole.trim().toLowerCase()
          : (
              firstRole?.name ||
              firstRole?.role ||
              firstRole?.type ||
              ""
            ).toLowerCase();
    }

    if (rolesStr) {
      return rolesStr.charAt(0).toUpperCase() + rolesStr.slice(1);
    }
  }

  // Priority 4: Check patron_type field
  if (userData?.patron_type) {
    const patronType = userData.patron_type.trim().toLowerCase();
    return patronType.charAt(0).toUpperCase() + patronType.slice(1);
  }

  // Priority 5: Check user_types array
  if (Array.isArray(userData?.user_types) && userData.user_types.length > 0) {
    const userTypeObj = userData.user_types[0];
    const typeStr =
      userTypeObj?.user_type || userTypeObj?.type || userTypeObj?.name || "";
    if (typeStr) {
      const userType = typeStr.trim().toLowerCase();
      return userType.charAt(0).toUpperCase() + userType.slice(1);
    }
  }

  // Default fallback
  return "Faculty";
};

// Helper function to check if user is faculty, visitor, or researcher
const isFacultyByUserType = (userData) => {
  const userType = getUserLibraryType(userData).toLowerCase();
  return (
    userType === "faculty" ||
    userType === "visitor" ||
    userType === "researcher"
  );
};

// Helper function to check if user is faculty (using multiple validation methods)
const isFacultyUser = (userData) => {
  // Method 1: Check by user_type field directly
  if (isFacultyByUserType(userData)) {
    return true;
  }

  // Method 2: Check by roles array
  if (userData?.roles) {
    const roles = Array.isArray(userData.roles)
      ? userData.roles
      : [userData.roles];

    return roles.some((role) => {
      const roleStr =
        typeof role === "string"
          ? role
          : role?.name || role?.role || role?.type || "";
      return roleStr.toLowerCase().includes("faculty");
    });
  }

  // Method 3: Check user_types array
  if (Array.isArray(userData?.user_types)) {
    return userData.user_types.some((userType) => {
      const typeStr =
        userType?.user_type || userType?.type || userType?.name || "";
      return typeStr.toLowerCase().includes("faculty");
    });
  }

  return false;
};

// Helper function to extract user types
const extractUserTypes = (userData) => {
  if (Array.isArray(userData.user_types) && userData.user_types.length > 0) {
    return userData.user_types
      .map((userType) => userType.user_type || userType.type || userType.name)
      .filter(Boolean)
      .join(", ");
  }
  return "";
};

// Helper function to get display role with fallback to user_types
const getDisplayRole = (userData) => {
  // First try to get roles
  let displayRole = "";
  if (Array.isArray(userData.roles) && userData.roles.length > 0) {
    displayRole = userData.roles
      .map((role) =>
        typeof role === "string"
          ? role
          : role.name || role.role || role.type || role.title || String(role)
      )
      .filter(Boolean)
      .join(", ");
  } else if (userData.roles && typeof userData.roles === "string") {
    displayRole = userData.roles;
  }

  // If no roles found, fallback to user_types
  if (!displayRole) {
    displayRole = extractUserTypes(userData);
  }

  return displayRole;
};

const ProfileAvatar = ({ fullName, imageUrl, loading }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (imageUrl) {
      setImageError(false);
      setImageLoading(true);
    }
  }, [imageUrl]);

  if (loading) {
    return (
      <div style={AVATAR_STYLE}>
        <Loader active inline size="small" />
      </div>
    );
  }

  const fullImageUrl = getFullImageUrl(imageUrl);

  return (
    <div className="profile-avatar" style={AVATAR_STYLE}>
      {fullImageUrl && !imageError ? (
        <>
          <img
            src={fullImageUrl}
            alt={`${fullName || "User"} profile`}
            style={IMAGE_STYLE}
            onLoad={() => {
              setImageLoading(false);
              setImageError(false);
            }}
            onError={(e) => {
              setImageError(true);
              setImageLoading(false);
            }}
          />
          {imageLoading && (
            <div style={LOADING_OVERLAY_STYLE}>
              <Loader active inline size="small" />
            </div>
          )}
        </>
      ) : (
        <Icon name="user" style={USER_ICON_STYLE} />
      )}
    </div>
  );
};

ProfileAvatar.propTypes = {
  fullName: PropTypes.string,
  imageUrl: PropTypes.string,
  loading: PropTypes.bool,
};

const FieldDisplay = ({ label, value, icon }) => (
  <div className="profile-field-group">
    <div className="profile-field-label">
      {icon && <Icon name={icon} />}
      {label}
    </div>
    <div className={value ? "profile-field-value" : "profile-field-empty"}>
      {value || "Not provided"}
    </div>
  </div>
);

FieldDisplay.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  icon: PropTypes.string,
};

const ContactSection = ({ profile }) => (
  <div>
    <div className="profile-section-title">Contact Information</div>
    <Grid stackable>
      <Grid.Row>
        <Grid.Column width={8}>
          <FieldDisplay
            label="Email Address"
            value={profile.email}
            icon="mail"
          />
        </Grid.Column>
        <Grid.Column width={8}>
          <FieldDisplay label="Username" value={profile.username} icon="user" />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={8}>
          <FieldDisplay
            label="Full Name"
            value={profile.full_name}
            icon="user outline"
          />
        </Grid.Column>
        <Grid.Column width={8}>
          <FieldDisplay
            label="Contact Number"
            value={profile.contact_number}
            icon="phone"
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </div>
);

ContactSection.propTypes = {
  profile: PropTypes.object.isRequired,
};

const AcademicSection = ({ profile }) => {
  // Check if user is faculty, visitor, or researcher (will hide Year and Semester for these users)
  const isUserFaculty = isFacultyUser(profile);

  return (
    <div>
      <div className="profile-section-title">Academic Information</div>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={8}>
            <FieldDisplay
              label="Department"
              value={profile.department}
              icon="building"
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <FieldDisplay label="Program" value={profile.program} icon="book" />
          </Grid.Column>
        </Grid.Row>

        {/* Only show Year and Semester if user is NOT faculty/visitor/researcher */}
        {!isUserFaculty && (
          <Grid.Row>
            <Grid.Column width={8}>
              <FieldDisplay
                label="Year Level"
                value={profile.year}
                icon="calendar"
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <FieldDisplay
                label="Semester"
                value={profile.semester}
                icon="calendar alternate"
              />
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row>
          <Grid.Column width={8}>
            <FieldDisplay
              label="Library Number"
              value={getLibraryId(profile)}
              icon="barcode"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

AcademicSection.propTypes = {
  profile: PropTypes.object.isRequired,
};

const RoleDisplay = ({ userData, title }) => {
  const displayRole = getDisplayRole(userData);
  // Check if user is faculty, visitor, or researcher
  const isUserFaculty = isFacultyUser(userData);

  // For faculty/visitor/researcher users, show appropriate title
  if (isUserFaculty) {
    const facultyTitle = title || "Faculty Member";
    return (
      <div
        className="profile-role-section"
        style={{
          marginBottom: "1rem",
          fontSize: "1.2rem",
          textTransform: "Uppercase",
        }}
      >
        {facultyTitle}
      </div>
    );
  }

  // For other users, use the original logic
  // Prioritize the detected role from user data over the title prop
  // Only use title if no role can be detected from user data
  const finalTitle = displayRole || title;

  if (!finalTitle) {
    return null;
  }

  return (
    <div
      className="profile-role-section"
      style={{
        marginBottom: "1rem",
        fontSize: "1.2rem",
        textTransform: "Uppercase",
      }}
    >
      {finalTitle}
    </div>
  );
};

RoleDisplay.propTypes = {
  userData: PropTypes.object,
  title: PropTypes.string,
};

// Faculty Library Section Component
const FacultyLibrarySection = ({
  profile,
  user,
  onLibraryIdUpdate,
  showLibraryManagement = true,
}) => {
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const isUserFaculty = isFacultyUser(user || profile);

  if (!isUserFaculty) {
    return null;
  }

  const libraryId = getLibraryId(user || profile);
  const userType = getUserLibraryType(user || profile);
  const libraryIdLabel = `${userType} Library ID`;

  const openBarcodeModal = () => {
    setShowBarcodeModal(true);
  };

  const closeBarcodeModal = () => {
    setShowBarcodeModal(false);
  };

  return (
    <div>
      <div
        className="profile-section-title"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>Library Information</span>
        {libraryId && (
          <Icon
            name="barcode"
            style={{
              cursor: "pointer",
              color: "#225745",
              fontSize: "1.2em",
              marginLeft: "0.5rem",
            }}
            onClick={openBarcodeModal}
            title="Show Barcode"
          />
        )}
      </div>
      <Grid stackable>
        {/* Show library ID field with dynamic label */}
        <Grid.Row>
          <Grid.Column width={16}>
            <FieldDisplay
              label={libraryIdLabel}
              value={libraryId}
              icon="id card"
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Barcode Modal */}
      <Modal
        open={showBarcodeModal}
        onClose={closeBarcodeModal}
        size="small"
        dimmer="blurring"
      >
        <Modal.Header>
          <Icon name="barcode" />
          {userType} Library Barcode
        </Modal.Header>
        <Modal.Content>
          <div style={{ textAlign: "center", padding: "1rem" }}>
            {libraryId && (
              <div>
                <div style={{ marginBottom: "1rem" }}>
                  <strong>Library ID: {libraryId}</strong>
                </div>
                <FacultyLibraryIdSection
                  user={user || profile}
                  onLibraryIdUpdate={onLibraryIdUpdate}
                  initialLibraryId={libraryId}
                  displayMode="barcode-only"
                  showTitle={false}
                  showDescription={false}
                  showInput={false}
                  showButtons={false}
                  showUpdateButton={false}
                  showClearButton={false}
                  showPrintButton={false}
                />
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeBarcodeModal} primary>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

FacultyLibrarySection.propTypes = {
  profile: PropTypes.object.isRequired,
  user: PropTypes.object,
  onLibraryIdUpdate: PropTypes.func,
  showLibraryManagement: PropTypes.bool,
};

const ProfileDisplay = ({
  profile,
  user,
  showAcademicInfo,
  title,
  fetchImageFromAPI,
  onLibraryIdUpdate,
  showLibraryManagement = false,
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [fetchedLibraryId, setFetchedLibraryId] = useState(null);

  const propImageUrl = user?.image_url || profile?.image_url;

  useEffect(() => {
    const loadUserInfo = async () => {
      if (propImageUrl) {
        setImageUrl(propImageUrl);
      }

      if (!fetchImageFromAPI) {
        return;
      }

      setImageLoading(true);

      const imageInfoPromise = profileApi
        .getImageInfo()
        .then((imageInfo) => {
          if (imageInfo?.image_url) {
            setImageUrl(imageInfo.image_url);
          }
        })
        .catch(() => {
          // Silently handle image info fetch errors
        });

      const userDataPromise = userApi
        .get()
        .then((userResponse) => {
          const userData = userResponse?.data;
          if (userData) {
            const foundLibraryId = getLibraryId(userData);
            setFetchedLibraryId(foundLibraryId);
            // Only set image URL if we don't already have one
            if (!propImageUrl && !imageUrl && userData.image_url) {
              setImageUrl(userData.image_url);
            }
          }
        })
        .catch(() => {
          // Silently handle user data fetch errors
        });

      await Promise.allSettled([imageInfoPromise, userDataPromise]);
      setImageLoading(false);
    };

    loadUserInfo();
    // Remove imageUrl from dependencies to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propImageUrl, fetchImageFromAPI]);

  const finalImageUrl = propImageUrl || imageUrl;
  const mergedProfile = {
    ...profile,
    ...user,
    library_id: fetchedLibraryId || user?.library_id,
  };

  const isUserFaculty = isFacultyUser(user || mergedProfile);

  // Default library ID update handler if none provided
  const handleLibraryIdUpdate = async (newLibraryId) => {
    if (onLibraryIdUpdate) {
      return await onLibraryIdUpdate(newLibraryId);
    } else {
      // Default implementation
      throw new Error("Library ID update handler not provided");
    }
  };

  return (
    <Container className="profile-container">
      <Segment className="profile-card">
        <div className="profile-header-section">
          <ProfileAvatar
            fullName={mergedProfile.full_name}
            imageUrl={finalImageUrl}
            loading={imageLoading}
          />

          <Header as="h2" className="profile-name-yellow">
            {mergedProfile.full_name || "User Profile"}
          </Header>
          <RoleDisplay userData={user || mergedProfile} title={title} />
        </div>
        <div className="profile-content-section">
          <ContactSection profile={mergedProfile} />

          {showAcademicInfo && (
            <>
              <Divider />
              <AcademicSection profile={mergedProfile} />
            </>
          )}

          {/* Show Faculty Library Section for faculty users */}
          {isUserFaculty && (
            <>
              <Divider />
              <FacultyLibrarySection
                profile={mergedProfile}
                user={user}
                onLibraryIdUpdate={handleLibraryIdUpdate}
                showLibraryManagement={showLibraryManagement}
              />
            </>
          )}
        </div>
      </Segment>
    </Container>
  );
};

ProfileDisplay.propTypes = {
  profile: PropTypes.object.isRequired,
  user: PropTypes.object,
  showAcademicInfo: PropTypes.bool,
  title: PropTypes.string,
  fetchImageFromAPI: PropTypes.bool,
  onLibraryIdUpdate: PropTypes.func,
  showLibraryManagement: PropTypes.bool,
};

ProfileDisplay.defaultProps = {
  user: {},
  showAcademicInfo: true,
  title: "",
  fetchImageFromAPI: true,
  onLibraryIdUpdate: null,
  showLibraryManagement: false,
};

export default ProfileDisplay;
